import { Node, Stage } from "konva/lib/Core";
import { nodeSize, scaleSize, scalePosition, WhiteboardShape, IWhiteboardShapeState } from "../shapes";
import { px, rgbToHsp } from "../util";
import { IShapeSelectionRenderer } from "./selection";
import { setTimeoutTimes } from "@gtmhub/util";
import { Util } from "konva/lib/Util";

export const SHAPE_SELECTED = "shape-selected";
export const SHAPE_UNSELECTED = "shape-unselected";

export const isDarkColor = (color: string): boolean => rgbToHsp(Util.getRGB(color)) < 190;

const pullMenuUp = 74;

const contextMenuElement = () => {
  const originalContextMenuEl = document.getElementById("shape-context-menu");
  if (!originalContextMenuEl) {
    throw new Error("Cannot find shape context menu element");
  }

  return originalContextMenuEl;
};

const positionContextMenu = (shape: Node & WhiteboardShape<IWhiteboardShapeState>): void => {
  // run multiple times until Angular.js has rendered the component
  setTimeoutTimes(
    5,
    () => {
      const stage = shape.getStage();
      // we might be removing the shape while running multiple
      // setTimeouts, so its stage will be null
      if (!stage) {
        return true;
      }

      const contextMenuEl = contextMenuElement();
      const scale = stage.scale();
      const shapePos = scalePosition(scale, shape.getPosition());
      const shapeSize = scaleSize(scale, nodeSize(shape));
      let contextMenuRect = contextMenuEl.getBoundingClientRect();

      contextMenuRect = contextMenuEl.getBoundingClientRect();

      let x = stage.x() + shapePos.x + (shapeSize.width - contextMenuRect.width) / 2;
      let y = stage.y() + shapePos.y - pullMenuUp;

      const minX = 0;
      const maxX = -contextMenuRect.width / 2;

      if (x < minX && x > maxX) {
        /*
          x = -10; minX = 0; maxX = -100.
          It's negative because we scroll to the right.
          This is added because we have a case when the context menu can be hidden from the left navigation.
         */
        x = minX;
      }

      if (shape.state.type === "image") {
        const shapeBoundaries = shape.getClientRect({ skipTransform: false });
        x = shapeBoundaries.x + (shapeBoundaries.width - contextMenuRect.width) / 2;
        y = shapeBoundaries.y - pullMenuUp;
      }

      contextMenuEl.style.top = px(y);
      contextMenuEl.style.left = px(x);
      contextMenuEl.style.visibility = "visible";

      return false;
    },
    20
  );
};

export const configureShapeContextMenu = <Renderer extends IShapeSelectionRenderer>(renderer: Renderer, stage: Stage): void => {
  stage.on("dragstart transformstart", () => {
    const selectedShapes = renderer.selectedShapes();
    if (selectedShapes.length === 1) {
      const contextMenuEl = contextMenuElement();
      contextMenuEl.style.visibility = "hidden";
    }
  });

  stage.on("dragend transformend", () => {
    const selectedShapes = renderer.selectedShapes();
    if (selectedShapes.length === 1) {
      positionContextMenu(selectedShapes[0]);
    }
  });

  stage.on("resize", () => {
    const selectedShapes = renderer.selectedShapes();
    if (selectedShapes.length === 1) {
      const contextMenuEl = contextMenuElement();
      if (contextMenuEl.style.visibility === "visible") {
        positionContextMenu(selectedShapes[0]);
      }
    }
  });

  let previousSelectedShape;

  const hideContextMenus = (): void => {
    const contextMenuEl = document.getElementById("shape-context-menu");
    if (contextMenuEl) {
      contextMenuEl.style.visibility = "hidden";
    }
  };

  stage.on("selectionChange", () => {
    const selectedShapes = renderer.selectedShapes();
    previousSelectedShape && previousSelectedShape.fire(SHAPE_UNSELECTED);

    if (selectedShapes.length === 1) {
      const shape = selectedShapes[0];
      previousSelectedShape = shape;

      positionContextMenu(shape);
      shape.fire(SHAPE_SELECTED);
    } else {
      previousSelectedShape = null;
      hideContextMenus(); // hide contextmenu should be after the update-shape event
    }
  });
};
